import { useState } from "react";

async function requestSuggestion({ comment, prompt, message, label }) {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/lab/comment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment, prompt, message, label }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = typeof data.detail === "string" ? data.detail : "La suggestion n’a pas abouti.";
    return { ok: false, reason: detail };
  }
  return { ok: true, suggestion: data };
}

export default function LabCommentSuggest({
  prompt,
  message,
  label,
  idPrefix = "lab",
  initialComment = "",
  onCommentChange,
  onUsePrompt,
  onUseMessage,
  onIntegrated,
}) {
  const [comment, setComment] = useState(initialComment);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const suggest = async () => {
    setError("");
    setNotice("");
    setSuggestion(null);
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const result = await requestSuggestion({
        comment: comment.trim(),
        prompt,
        message,
        label,
      });
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      setSuggestion(result.suggestion);
    } catch {
      setError("La suggestion n’a pas abouti. Réessaie.");
    } finally {
      setBusy(false);
    }
  };

  const accept = (kind) => {
    if (!suggestion) return false;
    const jobs = kind === "both" ? ["prompt", "message"] : [kind];
    let applied = 0;
    let blocked = "";
    for (const job of jobs) {
      if (job === "prompt" && !suggestion.change_prompt) continue;
      if (job === "message" && !suggestion.change_message) continue;
      const result = job === "prompt" ? onUsePrompt(suggestion.prompt) : onUseMessage(suggestion.message);
      if (result && result.ok === false) {
        blocked = result.reason || (job === "prompt" ? "Le prompt n’a pas été enregistré." : "Le message n’a pas été enregistré.");
        continue;
      }
      applied += 1;
    }
    if (!applied) {
      setNotice("");
      setError(blocked || "Aucun changement à enregistrer.");
      return false;
    }
    setError("");
    setSuggestion(null);
    setNotice(
      kind === "both" && applied === 2
        ? "Prompt et message suggérés enregistrés."
        : kind === "prompt" || (kind === "both" && suggestion.change_prompt && applied === 1 && !suggestion.change_message)
          ? "Prompt suggéré enregistré."
          : kind === "message"
            ? "Message suggéré enregistré."
            : "Suggestion enregistrée."
    );
    onIntegrated?.();
    return true;
  };

  return (
    <div className="mt-4" data-testid="lab-comment-suggest">
      <label className="block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]" htmlFor={`${idPrefix}-feedback`}>
        Commentaire
      </label>
      <textarea
        id={`${idPrefix}-feedback`}
        data-testid={`${idPrefix}-feedback`}
        value={comment}
        rows={2}
        onChange={(event) => {
          const next = event.target.value;
          setComment(next);
          setError("");
          setNotice("");
          onCommentChange?.(next);
        }}
        placeholder="Ex. : plus court, retire « virtuel », tutoiement plus chaleureux"
        className="mt-1 w-full resize-y rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 py-2.5 text-sm outline-none focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-[11px] text-[#829C8D]">
          Conservé dans ce navigateur, même après fermeture.
        </p>
        <button
          type="button"
          data-testid={`${idPrefix}-comment-suggest-button`}
          onClick={suggest}
          disabled={!comment.trim() || busy}
          className="rounded-full bg-[#122118] px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
        >
          {busy ? "Analyse…" : "Suggérer"}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert" data-testid={`${idPrefix}-comment-error`}>
          {error}
        </p>
      )}
      {notice && (
        <p className="mt-2 text-xs font-semibold text-[#0B8F6A]" data-testid={`${idPrefix}-comment-notice`}>
          {notice}
        </p>
      )}
      {suggestion && (
        <div className="mt-3 rounded-xl border border-[#C6EBD7] bg-[#F3FCF7] p-3" data-testid={`${idPrefix}-comment-suggestion`}>
          <p className="text-xs font-semibold text-[#122118]">{suggestion.summary}</p>
          {!suggestion.change_prompt && !suggestion.change_message && (
            <p className="mt-1 text-[11px] text-[#4a5f53]">Aucun texte à modifier pour ce commentaire.</p>
          )}
          {suggestion.change_prompt && (
            <div className="mt-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">Prompt suggéré</p>
              <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-[#122118]">{suggestion.prompt}</p>
            </div>
          )}
          {suggestion.change_message && (
            <div className="mt-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">Message suggéré</p>
              <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-[#122118]">{suggestion.message}</p>
            </div>
          )}
          {(suggestion.change_prompt || suggestion.change_message) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {suggestion.change_prompt && (
                <button
                  type="button"
                  data-testid={`${idPrefix}-comment-use-prompt`}
                  onClick={() => accept("prompt")}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-[#122118]"
                >
                  Utiliser le prompt
                </button>
              )}
              {suggestion.change_message && (
                <button
                  type="button"
                  data-testid={`${idPrefix}-comment-use-message`}
                  onClick={() => accept("message")}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-[#122118]"
                >
                  Utiliser le message
                </button>
              )}
              {suggestion.change_prompt && suggestion.change_message && (
                <button
                  type="button"
                  data-testid={`${idPrefix}-comment-use-both`}
                  onClick={() => accept("both")}
                  className="rounded-full bg-[#122118] px-3 py-1.5 text-[11px] font-semibold text-white"
                >
                  Utiliser les deux
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
